import React, {
    MouseEventHandler,
    useCallback,
    BaseSyntheticEvent,
} from 'react';
import {
    CreateParams,
    RaRecord,
    UpdateParams,
    useSaveContext,
    useNotify,
    useTranslate,
    TransformData,
} from 'react-admin';
import { Tooltip, IconButton } from '@mui/material';
import ContentSave from '@mui/icons-material/Save';
import { useFormContext } from 'react-hook-form';
import { UseMutationOptions } from 'react-query';

export const SaveRowButton = <
    RecordType extends RaRecord = RaRecord
>({
    handleSubmit,
    saving,
    submitOnEnter = true,
    mutationOptions,
    transform,
}: SaveRowButtonProps<RecordType>) => {
    const translate = useTranslate();
    const notify = useNotify();
    const form = useFormContext();
    const saveContext = useSaveContext();
    const hasSideEffects =
        (mutationOptions &&
            (mutationOptions?.onSuccess || mutationOptions?.onError)) ||
        transform;
    const type = !submitOnEnter || hasSideEffects ? 'button' : 'submit';
    const { id = 'new_record' } = form.getValues();

    const onClick: MouseEventHandler<HTMLButtonElement> = useCallback(
        async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            if (type === 'button') {
                // this button doesn't submit the form, so it doesn't trigger useIsFormInvalid in <FormContent>
                // therefore we need to check for errors manually
                const isFormValid = await form.trigger();
                if (!isFormValid) {
                    event.preventDefault();
                    notify('ra.message.invalid_form', { type: 'warning' });
                    return;
                }
                const values = form.getValues();
                saveContext?.save(values, { ...mutationOptions, transform });
            } else {
                handleSubmit(event);
            }
        },
        [
            type,
            form,
            saveContext,
            mutationOptions,
            transform,
            notify,
            handleSubmit,
        ]
    );

    const label = translate('ra.action.save', {
        _: 'ra.action.save',
    });

    return (
        <Tooltip title={label}>
            <IconButton
                aria-label={label}
                disabled={saving}
                onClick={onClick}
                size="small"
                color="primary"
                form={`row${id}`}
                type={type}
            >
                <ContentSave />
            </IconButton>
        </Tooltip>
    );
};

export interface SaveRowButtonProps<
    RecordType extends RaRecord = RaRecord
> {
    handleSubmit: (e: BaseSyntheticEvent) => void;
    mutationOptions?: UseMutationOptions<
        RecordType,
        unknown,
        CreateParams<RecordType> | UpdateParams<RecordType>
    >;
    saving?: boolean;
    submitOnEnter?: boolean;
    transform?: TransformData;
}
